// Route table shared by useRoutes (router/index.ts), the prerender (entry-server.tsx) and
// preloadRoute (main.tsx). Pages are lazyPage() so a prerendered route's chunk can be loaded
// before hydrateRoot (see _seo-kit/pattern/PATTERN.md, "Preload the route chunk").
import type { ComponentType } from 'react';
import { matchRoutes, type RouteObject } from 'react-router-dom';
import { lazyPage } from '../lib/lazyPage';

const HomePage = lazyPage(() => import('../pages/home/page'));
const CCMPage = lazyPage(() => import('../pages/ccm/page'));
const RPMPage = lazyPage(() => import('../pages/rpm/page'));
const AWVPage = lazyPage(() => import('../pages/awv/page'));
const BHIPage = lazyPage(() => import('../pages/bhi/page'));
const PCMPage = lazyPage(() => import('../pages/pcm/page'));
const TCMPage = lazyPage(() => import('../pages/tcm/page'));
const RTMPage = lazyPage(() => import('../pages/rtm/page'));
const CPMPage = lazyPage(() => import('../pages/cpm/page'));
const ContactPage = lazyPage(() => import('../pages/contact/page'));
const NotFoundPage = lazyPage(() => import('../pages/NotFound'));

type Page = { path: string; Component: ComponentType & { preload?: () => Promise<unknown> } };

/** The catch-all "*" (404) must stay last. */
const pages: Page[] = [
  { path: '/', Component: HomePage },
  { path: '/ccm', Component: CCMPage },
  { path: '/rpm', Component: RPMPage },
  { path: '/awv', Component: AWVPage },
  { path: '/bhi', Component: BHIPage },
  { path: '/pcm', Component: PCMPage },
  { path: '/tcm', Component: TCMPage },
  { path: '/rtm', Component: RTMPage },
  { path: '/cpm', Component: CPMPage },
  { path: '/contact', Component: ContactPage },
  { path: '*', Component: NotFoundPage },
];

const routes: RouteObject[] = pages.map(({ path, Component }) => ({ path, element: <Component /> }));

/** Loads the lazy page chunk matching `pathname`; main.tsx awaits it before hydrateRoot. */
export function preloadRoute(pathname: string): Promise<unknown> {
  const matches = matchRoutes(pages, pathname, __BASE_PATH__) ?? [];
  return Promise.all(matches.map((m) => (m.route as Page).Component.preload?.()));
}

export default routes;
